import React, { useState, useCallback, useEffect, useImperativeHandle, forwardRef } from 'react'
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride'

const TOUR_DONE_KEY = 'ugc4you_profile_tour_done'

const steps = [
  {
    target: 'body',
    content: (
      <div>
        <h2 className="text-xl sm:text-2xl font-bold mb-4">Welkom bij je Profiel!</h2>
        <p className="text-base sm:text-lg mb-2">
          Een volledig profiel is <strong>essentieel</strong> om gevonden te worden door bedrijven.
        </p>
        <p className="text-gray-600">
          We lopen samen door alle onderdelen heen.
        </p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="profile-edit"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Bewerken</h3>
        <p>
          Klik op <strong>Bewerken</strong> om je profiel aan te passen. Na het wijzigen klik je op Opslaan.
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="profile-basic"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Basis Informatie</h3>
        <p>
          Vul je voornaam, achternaam, creatornaam, stad en geboortedatum in. Dit helpt bedrijven om je te vinden.
        </p>
        <p className="mt-2 text-sm text-amber-600 font-semibold">
          Tip: Gebruik je echte naam voor meer vertrouwen!
        </p>
      </div>
    ),
    placement: 'bottom',
  },
  {
    target: '[data-tour="profile-bio"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Bio</h3>
        <p>
          Schrijf een korte maar krachtige bio over jezelf. Vertel wat voor content je maakt en wat je uniek maakt.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="profile-niches"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Specialisaties</h3>
        <p>
          Selecteer de niches waarin je actief bent. Bedrijven zoeken creators op basis van specialisatie.
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="profile-social"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Social Media</h3>
        <p>
          Koppel je Instagram, TikTok en YouTube accounts. Je followeraantal is zichtbaar voor bedrijven.
        </p>
        <p className="mt-2 text-sm text-amber-600 font-semibold">
          Tip: Meer platforms = meer zichtbaarheid!
        </p>
      </div>
    ),
    placement: 'top',
  },
  {
    target: '[data-tour="profile-pricing"]',
    content: (
      <div>
        <h3 className="text-xl font-bold mb-2">Prijzen</h3>
        <p>
          Stel je basistarief in. Dit geeft bedrijven een indicatie van je prijsniveau. Je kunt aangeven of het onderhandelbaar is.
        </p>
      </div>
    ),
    placement: 'top',
  },
]

// Mobiele afstelling: react-joyride gebruikt standaard een tooltip van 380px, breder dan
// een telefoonscherm. Daarom breedte begrensd op de viewport en compactere padding/knoppen
// onder 768px. De tour start op mobiel gewoon automatisch (creators werken vooral op telefoon).
const isMobileViewport = () => typeof window !== 'undefined' && window.innerWidth < 768
const tourWidth = () => (typeof window !== 'undefined' ? Math.min(380, window.innerWidth - 32) : 380)

const ProfileTour = forwardRef(({ hasCompletedProfile }, ref) => {
  const [run, setRun] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const compact = isMobileViewport()

  useEffect(() => {
    const done = localStorage.getItem(TOUR_DONE_KEY)
    if (!done && !hasCompletedProfile) {
      const timer = setTimeout(() => {
        setRun(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCallback = useCallback((data) => {
    const { action, index, status, type } = data

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false)
      setStepIndex(0)
      localStorage.setItem(TOUR_DONE_KEY, 'true')
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1))
    }
  }, [])

  const restartTour = useCallback(() => {
    setStepIndex(0)
    setTimeout(() => setRun(true), 300)
  }, [])

  useImperativeHandle(ref, () => ({ restartTour }), [restartTour])

  return (
    <Joyride
      steps={compact ? steps.map((st) => (st.placement === 'top' ? { ...st, placement: 'center' } : st)) : steps}
      stepIndex={stepIndex}
      run={run}
      continuous
      showProgress
      showSkipButton
      scrollOffset={80}
      spotlightPadding={8}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: '#0ea5e9',
          zIndex: 10000,
          width: tourWidth(),
        },
        tooltip: {
          borderRadius: '12px',
          padding: compact ? '16px' : '24px',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        buttonNext: {
          backgroundColor: '#0ea5e9',
          borderRadius: '8px',
          padding: compact ? '10px 14px' : '10px 20px',
          fontSize: compact ? '14px' : '16px',
          fontWeight: 'bold',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: '10px',
        },
        buttonSkip: {
          color: '#9ca3af',
        },
      }}
      locale={{
        back: 'Vorige',
        close: 'Sluiten',
        last: 'Afronden',
        next: 'Volgende',
        nextLabelWithProgress: compact ? 'Volgende ({step}/{steps})' : 'Volgende ({step} van {steps})',
        skip: 'Overslaan',
      }}
    />
  )
})

export default ProfileTour
